import { db } from "../lib/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  onSnapshot,
  query,
  where,
  serverTimestamp,
} from "../lib/firestoreClient";
import { productRepository } from "./productRepository";
import { errorDev } from "../utils/logger";

const USERS = "users";
const USER_WISHLIST = "wishlist";
const LEGACY_WISHLIST_COLLECTIONS = ["wishlists"];

function mapDoc(docRef, collectionName) {
  return { id: docRef.id, collection: collectionName, ...docRef.data() };
}

// A wishlist row is renderable when it names a product, either directly
// (`productId`) or through an embedded legacy product snapshot.
function rowProductId(row) {
  return row?.productId || row?.product?.id || null;
}

function dedupeByProductId(rows) {
  const byProductId = new Map();
  rows.forEach((row) => {
    const productId = rowProductId(row);
    if (!productId || byProductId.has(productId)) return;
    byProductId.set(productId, row);
  });
  return Array.from(byProductId.values());
}

function userWishlistRef(userId, wishId = null) {
  if (wishId) return doc(db, USERS, String(userId), USER_WISHLIST, String(wishId));
  return collection(db, USERS, String(userId), USER_WISHLIST);
}

export const wishlistRepository = {
  canonicalPath: "users/{userId}/wishlist/{wishId}",
  legacyPaths: ["wishlists/{wishId}"],

  buildPayload({ userId, productId, product = {}, sellerId = null }) {
    return {
      userId,
      productId: String(productId),
      productTitle: product.title || product.name || "",
      productImage: product.images?.[0] || product.image || "",
      sellerId: sellerId || product.poster?.userId || product.userId || null,
      createdAt: serverTimestamp(),
    };
  },

  async getById(userId, wishId) {
    if (!userId || !wishId) return null;
    const snap = await getDoc(userWishlistRef(userId, wishId));
    return snap.exists() ? mapDoc(snap, USERS) : null;
  },

  async list(userId) {
    if (!userId) return [];
    const snap = await getDocs(query(userWishlistRef(userId), where("userId", "==", String(userId))));
    return snap.docs.map((d) => mapDoc(d, USERS));
  },

  async listLegacy(userId, onError = null) {
    if (!userId) return [];
    const rows = [];

    for (const name of LEGACY_WISHLIST_COLLECTIONS) {
      try {
        const snap = await getDocs(query(collection(db, name), where("userId", "==", String(userId))));
        snap.docs.forEach((d) => rows.push(mapDoc(d, name)));
      } catch (error) {
        if (onError) onError(error, name);
        else errorDev("Legacy wishlist read failed:", error);
      }
    }

    return rows;
  },

  async listAll(userId) {
    const rows = dedupeByProductId([...(await this.list(userId)), ...(await this.listLegacy(userId))]);
    const products = await productRepository.getByIds(rows.map((row) => row.productId));
    const productMap = new Map(products.map((product) => [product.id, product]));

    return rows
      .map((row) => ({
        wishlistId: row.id,
        collection: row.collection,
        product: productMap.get(row.productId) || null,
      }))
      .filter((entry) => entry.product);
  },

  // New wishlist writes are canonical-only. The legacy `wishlists` collection is
  // read for historical documents but never written to, so new records cannot
  // re-introduce the deprecated shape.
  async add(userId, productId, product = {}, sellerId = null) {
    const payload = this.buildPayload({ userId, productId, product, sellerId });
    await setDoc(userWishlistRef(userId, productId), payload);
    return payload;
  },

  async remove(userId, wishId) {
    if (!userId || !wishId) return;
    await deleteDoc(userWishlistRef(userId, wishId));
  },

  async removeWithLegacyFallback(userId, wishId) {
    if (!userId || !wishId) return;
    await this.remove(userId, wishId).catch(() => null);
    await this.removeFromLegacyCollections(wishId);
  },

  async removeFromLegacyCollections(wishId) {
    if (!wishId) return;
    await Promise.all(
      LEGACY_WISHLIST_COLLECTIONS.map((name) => deleteDoc(doc(db, name, String(wishId))).catch(() => null))
    );
  },

  subscribe(userId, callback, onError = null) {
    if (!userId) {
      callback([]);
      return () => {};
    }
    return onSnapshot(
      query(userWishlistRef(userId), where("userId", "==", String(userId))),
      (snap) => callback(snap.docs.map((d) => mapDoc(d, USERS))),
      (error) => {
        errorDev("Wishlist listener failed:", error);
        if (onError) onError(error, USER_WISHLIST);
      }
    );
  },

  subscribeLegacy(userId, callback, onError = null) {
    if (!userId) {
      callback([]);
      return () => {};
    }

    const unsubscribers = LEGACY_WISHLIST_COLLECTIONS.map((name) =>
      onSnapshot(
        query(collection(db, name), where("userId", "==", String(userId))),
        (snap) => callback(snap.docs.map((d) => mapDoc(d, name))),
        (error) => {
          errorDev("Legacy wishlist listener failed:", error);
          if (onError) onError(error, name);
        }
      )
    ).filter((unsub) => typeof unsub === "function");

    return () => unsubscribers.forEach((unsub) => unsub());
  },

  subscribeAll(userId, callback, onError = null) {
    if (!userId) {
      callback([]);
      return () => {};
    }

    const rowsBySource = new Map();
    const unsubscribers = [];

    const reportError = (error, source) => {
      if (onError) onError(error, source);
      else errorDev("Wishlist subscription failed:", error);
    };

    const emit = () => {
      const rows = Array.from(rowsBySource.values()).flat().sort((a, b) => {
        const aTime = a?.createdAt?.toMillis?.() || new Date(a?.createdAt || 0).getTime() || 0;
        const bTime = b?.createdAt?.toMillis?.() || new Date(b?.createdAt || 0).getTime() || 0;
        return bTime - aTime;
      });
      callback(rows);
    };

    const attach = (source, subscribe) => {
      try {
        const unsubscribe = subscribe(
          userId,
          (rows) => {
            rowsBySource.set(source, (rows || []).filter((row) => rowProductId(row)));
            emit();
          },
          (error) => reportError(error, source)
        );
        // Only a callable teardown may be queued. Firestore always returns one,
        // but a partially-failed or mocked source can return nothing, and an
        // uncallable entry would make the whole unsubscribe throw and leak the
        // other listeners.
        if (typeof unsubscribe === "function") unsubscribers.push(unsubscribe);
      } catch (error) {
        // Keep whatever the other source already delivered. Clearing here used to
        // blank a fully loaded wishlist when one compatibility stream failed.
        if (!rowsBySource.has(source)) rowsBySource.set(source, []);
        reportError(error, source);
        emit();
      }
    };

    attach("canonical", this.subscribe.bind(this));
    attach("legacy", this.subscribeLegacy.bind(this));

    return () => unsubscribers.forEach((unsub) => unsub());
  },
};

export default wishlistRepository;
